// ============================================================================
// Adam Platform — Plugin Health Panel (BUCKET I.7)
// ============================================================================
// Renders the plugin telemetry snapshot flowing in from gateway via CP
// (BUCKET H pipeline). Shows per-plugin status, version, last seen, and a
// human-readable error reason when a plugin enters error status.
//
// Principles:
//   * P4 (truthful-disabled): empty plugin list shows "No plugins reporting",
//     not a fake "All healthy" placebo.
//   * P11 (per-component ownership): owns the `PluginTelemetryEntry` /
//     `PluginErrorReason` wire types — DeploymentHealth used to declare them
//     locally, but the panel is the rendering owner.
// ============================================================================
import { useQuery } from "@tanstack/react-query";
import { Card, SectionHeader, StatusDot, Spinner, QueryError } from "./index";
import { apiGet } from "../lib/api";

// ----------------------------------------------------------------------------
// Wire types — mirror the cross-service contract from BUCKET H. Source of
// truth lives in `hank/src/api/lib/runtime-security.ts` (lines 45-88). Adam
// is independent of hank/src/, so the shape is duplicated here. Re-export so
// downstream callers (e.g. DeploymentHealth) can reuse these without a second
// copy.
// ----------------------------------------------------------------------------
export type PluginErrorReason =
  | {
      kind: "incompatible";
      target: "runtime" | "hank" | "gateway";
      requiredRange: string;
      actualVersion: string;
    }
  | { kind: "malformed_range"; target: string; raw: string }
  | { kind: "unknown_target"; target: string };

export interface PluginTelemetryEntry {
  pluginId: string;
  status: "loaded" | "disabled" | "error";
  enabled: boolean;
  version?: string;
  lastSeen?: string;
  errorReason?: PluginErrorReason;
}

export interface PluginTelemetryResponse {
  deploymentId: string;
  plugins: PluginTelemetryEntry[];
}

// ----------------------------------------------------------------------------
// Display helpers
// ----------------------------------------------------------------------------
const STATUS_DOT: Record<PluginTelemetryEntry["status"], "healthy" | "degraded" | "down"> = {
  loaded: "healthy",
  disabled: "degraded",
  error: "down",
};

/**
 * Build the human copy for an error reason. Kept here so the panel (and its
 * test) is the single source of truth for phrasing. If the plugin row is in
 * error status but no structured reason was captured, we fall back to a
 * truthful "no reason captured" string rather than an empty cell.
 */
export function formatPluginErrorReason(
  entry: Pick<PluginTelemetryEntry, "status" | "errorReason">,
): string {
  const reason = entry.errorReason;
  if (!reason) {
    return entry.status === "error" ? "error (no reason captured)" : "";
  }
  switch (reason.kind) {
    case "incompatible":
      return `pins ${reason.target} ${reason.requiredRange}, got ${reason.actualVersion}`;
    case "malformed_range":
      return `manifest has malformed requires.${reason.target}: "${reason.raw}"`;
    case "unknown_target":
      return `unknown target "${reason.target}" in requires block`;
    default:
      // Exhaustiveness guard — new reason kinds should extend the switch.
      return "unrecognized error reason";
  }
}

/**
 * ISO timestamp → relative-time string ("5m ago", "2h ago"). Deliberately
 * simple — Adam doesn't ship a relative-time helper today, and adding a full
 * i18n library is out of scope for I.7.
 */
export function formatRelativeTime(iso: string | undefined, now: Date = new Date()): string {
  if (!iso) return "never";
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  if (Number.isNaN(diffMs)) return "never";
  if (diffMs < 0) return "just now";
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------
interface PluginHealthPanelProps {
  deploymentId: string;
}

export function PluginHealthPanel({ deploymentId }: PluginHealthPanelProps) {
  const { data, isLoading, error } = useQuery<PluginTelemetryResponse>({
    queryKey: ["telemetry-plugins", deploymentId],
    queryFn: () => apiGet<PluginTelemetryResponse>(`/api/telemetry/plugins/${deploymentId}`),
    enabled: !!deploymentId,
    refetchInterval: 60_000,
  });

  if (error) {
    return (
      <Card className="mb-6">
        <SectionHeader title="Plugins" subtitle="Live from gateway" />
        <QueryError error={error as Error} queryKeys={[["telemetry-plugins", deploymentId]]} />
      </Card>
    );
  }

  if (isLoading && !data) {
    return (
      <Card className="mb-6">
        <SectionHeader title="Plugins" subtitle="Live from gateway" />
        <div className="flex items-center gap-2 py-4" style={{ color: "var(--c-text-faint)" }}>
          <Spinner size={16} />
          <span className="text-xs">Loading plugin status…</span>
        </div>
      </Card>
    );
  }

  const plugins = data?.plugins ?? [];
  const subtitle = plugins.length === 0 ? "No plugins reporting" : "Live from gateway";

  return (
    <Card className="mb-6">
      <SectionHeader title="Plugins" subtitle={subtitle} />
      {plugins.length === 0 ? (
        // P4: truthful disabled state — we explicitly DO NOT render a fake
        // "All healthy" placebo here. If gateway hasn't reported plugins, we
        // say so plainly so operators know the panel is waiting on data, not
        // silently confirming everything is fine.
        <p className="text-xs" style={{ color: "var(--c-text-faint)" }}>
          No plugins reporting — gateway hasn't sent a snapshot yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
                <th
                  className="text-left py-2 pr-3 text-xs font-medium"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Plugin ID
                </th>
                <th
                  className="text-left py-2 pr-3 text-xs font-medium"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Status
                </th>
                <th
                  className="text-left py-2 pr-3 text-xs font-medium"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Version
                </th>
                <th
                  className="text-left py-2 pr-3 text-xs font-medium"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Last seen
                </th>
                <th
                  className="text-left py-2 text-xs font-medium"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Error reason
                </th>
              </tr>
            </thead>
            <tbody>
              {plugins.map((p) => (
                <tr
                  key={p.pluginId}
                  data-testid={`plugin-row-${p.pluginId}`}
                  style={{ borderBottom: "1px solid var(--c-border)" }}
                >
                  <td className="py-2 pr-3 font-mono text-xs" style={{ color: "var(--c-text)" }}>
                    {p.pluginId}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center gap-2">
                      <StatusDot status={STATUS_DOT[p.status]} />
                      <span className="text-xs" style={{ color: "var(--c-text)" }}>
                        {p.status}
                      </span>
                    </span>
                  </td>
                  <td
                    className="py-2 pr-3 font-mono text-xs"
                    style={{ color: "var(--c-text-secondary)" }}
                  >
                    {p.version ?? "—"}
                  </td>
                  <td
                    className="py-2 pr-3 text-xs"
                    style={{ color: "var(--c-text-secondary)" }}
                    title={p.lastSeen ?? ""}
                  >
                    {formatRelativeTime(p.lastSeen)}
                  </td>
                  <td className="py-2 text-xs" style={{ color: "var(--c-text-secondary)" }}>
                    {formatPluginErrorReason(p) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default PluginHealthPanel;
